import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  FileText,
  Flag,
  Globe,
  Map,
  Package,
  Play,
  Rocket,
  Settings,
  Shield,
  Star,
  Target,
  Users,
  Zap,
  Layers
} from 'lucide-react';
import ArchitectureLayers from '../pages/ArchitectureLayers';

interface Deliverable {
  id: string;
  name: string;
  description: string;
  priority: 'critical' | 'high' | 'medium';
  effort: string;
  dependencies: string[];
  status: 'planned' | 'in-progress' | 'completed';
  owner: string;
  keyAssumptions: string[];
}

interface KPI {
  name: string;
  target: string;
  current: string;
}

interface Milestone {
  id: string;
  name: string;
  date: string;
}

interface Quarter {
  id: string;
  name: string;
  theme: string;
  deliverables: Deliverable[];
  milestones: Milestone[];
  kpis: KPI[];
}

interface DeliveryPhaseProps {
  onComplete?: (results: any) => void;
  initialState?: {
    activeTab?: string;
    selectedQuarter?: any;
  };
  onStateChange?: (state: any) => void;
  isCompleted?: boolean;
  phaseResults?: any;
}

const DeliveryPhase: React.FC<DeliveryPhaseProps> = ({ 
  onComplete, 
  initialState, 
  onStateChange,
  isCompleted,
  phaseResults
}) => {
  const [activeTab, setActiveTab] = useState(initialState?.activeTab || 'roadmap');
  const [selectedQuarter, setSelectedQuarter] = useState<Quarter | null>(null);
  const [deliveryComplete, setDeliveryComplete] = useState(false);

  // If the phase is already completed, show the completed state immediately
  useEffect(() => {
    if (isCompleted && phaseResults) {
      setDeliveryComplete(true);
    }
  }, [isCompleted, phaseResults]);

  const roadmap: Quarter[] = [
    {
      id: 'q3',
      name: 'Q3 2025',
      theme: 'Homomorphic Encryption Core & Basic ML Ops',
      deliverables: [
        {
          id: 'd1-1',
          name: 'Core Homomorphic Encryption Library',
          description: 'High-performance C++/Rust library for core HE operations (BFV/CKKS).',
          priority: 'critical',
          effort: '10 weeks',
          dependencies: [],
          status: 'planned',
          owner: 'Cryptography R&D Team',
          keyAssumptions: ['Performance of core operations will meet theoretical benchmarks.', 'Chosen HE scheme is appropriate for target ML models.']
        },
        {
          id: 'd1-2',
          name: 'Proof-of-Concept: Encrypted Linear Regression',
          description: 'Train and execute a simple linear regression model on fully encrypted data.',
          priority: 'high',
          effort: '8 weeks',
          dependencies: ['d1-1'],
          status: 'planned',
          owner: 'ML Research Team',
          keyAssumptions: ['Accuracy on encrypted data will be within 2% of plaintext model.', 'Training time is acceptable for PoC purposes.']
        },
        {
          id: 'd1-3',
          name: 'Secure Data Ingestion API',
          description: 'API endpoint for uploading, encrypting, and storing datasets securely.',
          priority: 'high',
          effort: '6 weeks',
          dependencies: [],
          status: 'planned',
          owner: 'Platform API Team',
          keyAssumptions: ['API can handle expected payload sizes without timeouts.', 'Encryption process is integrated seamlessly.']
        }
      ],
      milestones: [
        { id: 'm1-1', name: 'HE Library Alpha Release', date: 'Aug 15, 2025' },
        { id: 'm1-2', name: 'PoC Demo to Stakeholders', date: 'Sep 30, 2025' },
      ],
      kpis: [
        { name: 'HE Operation Speed', target: '<10ms for key ops', current: 'N/A' },
        { name: 'API Uptime', target: '99.5%', current: 'N/A' }
      ]
    },
    {
      id: 'q4',
      name: 'Q4 2025',
      theme: 'Advanced ML Models & Performance',
      deliverables: [
        {
          id: 'd2-1',
          name: 'Encrypted Neural Network Support (Simple)',
          description: 'Support for training simple deep learning models (2-3 layers) on encrypted data.',
          priority: 'critical',
          effort: '12 weeks',
          dependencies: ['d1-1'],
          status: 'planned',
          owner: 'ML Research Team',
          keyAssumptions: ['Noise accumulation in multi-layer networks can be managed.', 'Activation functions can be approximated efficiently.']
        },
        {
          id: 'd2-2',
          name: 'Optimized Circuits for ML Operations',
          description: 'Reduce computational depth for common ML operations like matrix multiplication.',
          priority: 'high',
          effort: '8 weeks',
          dependencies: ['d1-1'],
          status: 'planned',
          owner: 'Cryptography R&D Team',
          keyAssumptions: ['Circuit optimization techniques will yield at least a 30% performance gain.']
        },
        {
          id: 'd2-3',
          name: 'Internal Pilot with Synthetic Data',
          description: 'Run an end-to-end pilot using synthetic financial or healthcare data.',
          priority: 'medium',
          effort: '6 weeks',
          dependencies: ['d2-1'],
          status: 'planned',
          owner: 'Product Team',
          keyAssumptions: ['Synthetic data accurately represents real-world data characteristics.']
        }
      ],
      milestones: [
        { id: 'm2-1', name: 'Neural Network Training Alpha', date: 'Nov 20, 2025' },
        { id: 'm2-2', name: 'Performance Benchmark Report', date: 'Dec 25, 2025' },
      ],
      kpis: [
        { name: 'Encrypted Training Time', target: '<5x plaintext', current: 'N/A' },
        { name: 'Model Accuracy Loss', target: '<2%', current: 'N/A' }
      ]
    },
    {
      id: 'q1',
      name: 'Q1 2026',
      theme: 'Developer Platform & API Ecosystem',
      deliverables: [
        {
          id: 'd3-1',
          name: 'Python SDK for Data Scientists',
          description: 'High-level Python library to abstract away the complexity of homomorphic encryption.',
          priority: 'critical',
          effort: '10 weeks',
          dependencies: ['d1-1', 'd2-1'],
          status: 'planned',
          owner: 'Platform API Team',
          keyAssumptions: ['The SDK abstraction does not significantly degrade performance.', 'The API is intuitive for data scientists with no crypto background.']
        },
        {
          id: 'd3-2',
          name: 'Secure Inference Engine API',
          description: 'API for making predictions on new encrypted data points using trained models.',
          priority: 'high',
          effort: '8 weeks',
          dependencies: ['d2-1'],
          status: 'planned',
          owner: 'Platform API Team',
          keyAssumptions: ['Inference latency meets the requirements for target use cases.']
        },
        {
          id: 'd3-3',
          name: 'Developer Portal & Documentation',
          description: 'Launch of a comprehensive developer portal with guides and API reference.',
          priority: 'medium',
          effort: '6 weeks',
          dependencies: ['d3-1'],
          status: 'planned',
          owner: 'DevRel Team',
          keyAssumptions: ['Documentation is clear enough to enable self-service onboarding.']
        }
      ],
      milestones: [
        { id: 'm3-1', name: 'Python SDK Beta Release', date: 'Feb 15, 2026' },
        { id: 'm3-2', name: 'Developer Portal Launch', date: 'Mar 28, 2026' },
      ],
      kpis: [
        { name: 'Time to First Prediction (DevEx)', target: '<30 minutes', current: 'N/A' },
        { name: 'API Requests per Second', target: '100 RPS', current: 'N/A' }
      ]
    },
    {
      id: 'q2',
      name: 'Q2 2026',
      theme: 'Enterprise Pilot & Launch Readiness',
      deliverables: [
        {
          id: 'd4-1',
          name: 'Onboard First Enterprise Pilot Customer',
          description: 'Work with a major healthcare or financial institution for a real-world pilot.',
          priority: 'critical',
          effort: '12 weeks',
          dependencies: ['d3-1', 'd3-2'],
          status: 'planned',
          owner: 'Sales & Product Team',
          keyAssumptions: ['A suitable pilot partner can be signed with reasonable legal terms.', 'Real-world data reveals no unexpected performance bottlenecks.']
        },
        {
          id: 'd4-2',
          name: 'Multi-Party Computation Support',
          description: 'Enable multiple parties to collaboratively train a model on their encrypted data.',
          priority: 'high',
          effort: '10 weeks',
          dependencies: ['d2-1'],
          status: 'planned',
          owner: 'Cryptography R&D Team',
          keyAssumptions: ['Network latency does not make MPC impractical.', 'The protocol is secure against collusion.']
        },
        {
          id: 'd4-3',
          name: 'SOC 2 Type 1 Compliance',
          description: 'Complete audit and documentation for SOC 2 Type 1 compliance.',
          priority: 'medium',
          effort: '8 weeks',
          dependencies: [],
          status: 'planned',
          owner: 'Security & Compliance Team',
          keyAssumptions: ['Internal controls and processes are mature enough to pass the audit.']
        }
      ],
      milestones: [
        { id: 'm4-1', name: 'Pilot Program Kick-off', date: 'Apr 10, 2026' },
        { id: 'm4-2', name: 'SOC 2 Audit Complete', date: 'Jun 20, 2026' },
      ],
      kpis: [
        { name: 'Pilot Customer Satisfaction', target: 'NPS > 50', current: 'N/A' },
        { name: 'Compliance Controls Implemented', target: '100%', current: 'N/A' }
      ]
    }
  ];

  const executiveSummary = {
    overview: "This roadmap outlines the multi-year strategy to develop and commercialize a groundbreaking Homomorphic Machine Learning (HML) Platform. By enabling computation on encrypted data, we unlock new possibilities in privacy-sensitive domains like healthcare, finance, and defense, establishing a new paradigm for secure AI.",
    totalInvestment: "$15M (3 Years)",
    expectedROI: "350% over 5 years",
    marketOpportunity: "Projected $12.8B addressable market by 2028 for privacy-enhancing technologies (PETs).",
    competitiveAdvantages: [
      'Proprietary, high-performance homomorphic encryption scheme',
      'Seamless integration with existing MLOps pipelines via a Python SDK',
      'First-mover advantage in a technically complex, high-barrier-to-entry field',
      'Built-in compliance-by-design for GDPR, HIPAA, etc.'
    ],
    riskMitigation: [
      'Focus R&D on performance optimization to ensure commercial viability',
      'Engage with regulators and standards bodies early to build trust',
      'Target specific, high-value use cases for initial market entry',
      'Build a world-class cryptography and ML research team'
    ],
    financialSummary: {
      'Year 1 (2025)': { revenue: '$0.5M', rdCost: '$8M', grossMargin: '-1500%' },
      'Year 2 (2026)': { revenue: '$5M', rdCost: '$6M', grossMargin: '-20%' },
      'Year 3 (2027)': { revenue: '$25M', rdCost: '$7M', grossMargin: '72%' }
    }
  };

  const implementationDetails = {
    governance: {
        coreTeam: "1 Product Lead, 1 Eng. Lead, 1 Cryptography Lead, 1 ML Lead",
        steeringCommittee: "CTO, CPO, Head of R&D, VP Engineering",
        communication: "Weekly Core Team Sync, Bi-weekly Steering Committee Review, Monthly All-Hands Update"
    },
    goToMarket: {
      launchTiers: [
        "**Tier 1 (Internal):** Dogfooding with internal teams for security analysis.",
        "**Tier 2 (Alpha):** Invite-only for academic researchers and friendly partners.",
        "**Tier 3 (Beta):** Limited access for signed pilot customers in target verticals.",
        "**Tier 4 (GA):** Public launch targeting enterprise data science teams."
      ],
      keyActivities: [
        "Publish academic research to build credibility.",
        "Present at top-tier security and AI conferences (e.g., NeurIPS, Black Hat).",
        "Develop detailed whitepapers for target industries (Healthcare, Finance).",
        "Build a technical pre-sales team to support enterprise pilots."
      ]
    }
  };

  const documentationHub = [
    { title: 'Technical Specifications', icon: Settings, desc: 'Complete system architecture for the Homomorphic Encryption Engine.', version: '0.8', status: 'Draft' },
    { title: 'API Documentation', icon: Globe, desc: 'Comprehensive API reference for data ingestion and secure inference.', version: '0.5', status: 'In Progress' },
    { title: 'Compliance Framework', icon: Shield, desc: 'Security controls and documentation for SOC 2 Type 1 readiness.', version: '0.2', status: 'Not Started' },
    { title: 'Developer Guides', icon: Users, desc: 'Onboarding guides and tutorials for the Python SDK.', version: '0.1', status: 'Not Started' },
  ];
  
  const completePhase = () => {
    try {
      const results = {
        roadmap,
        executiveSummary,
        completedAt: new Date().toISOString(),
        status: 'completed'
      };
      console.log("DeliveryPhase - Phase Complete");
      console.log("DeliveryPhase - onComplete function exists:", typeof onComplete === 'function');
      
      if (onComplete) {
        onComplete(results);
      } else {
        console.error("DeliveryPhase - onComplete function is not defined");
      }
    } catch (error) {
      console.error("DeliveryPhase - Error in completePhase:", error);
    }
  };

  const tabs = [
    { id: 'roadmap', label: 'Product Roadmap', icon: Map },
    { id: 'summary', label: 'Executive Summary', icon: FileText },
    { id: 'implementation', label: 'Implementation Guide', icon: Settings },
    { id: 'documentation', label: 'Documentation', icon: Package },
    { id: 'architecture', label: 'Architecture', icon: Layers }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'planned': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'not-started': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-8 bg-bg-light min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-deep-green uppercase tracking-wide font-fjalla">Product Delivery Plan</h1>
            <p className="text-lg text-text-secondary mt-2">Homomorphic Machine Learning Platform</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              console.log("DeliveryPhase - Button clicked!");
              completePhase();
            }}
            className="flex items-center space-x-2 sqsft-gradient-primary text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <span>Mark Phase as Complete</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>

        <div>
          {/* Navigation Tabs */}
          <div className="flex space-x-2 mb-8 bg-white/70 p-2 rounded-xl backdrop-blur-xl border border-border-color">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-4 py-2 rounded-lg text-sm font-roboto-condensed font-medium transition-colors duration-300 w-full flex items-center justify-center space-x-2 ${
                  activeTab === tab.id ? 'text-white' : 'text-text-secondary hover:bg-gray-200/50'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="active-tab-indicator"
                    className="absolute inset-0 sqsft-gradient-primary rounded-md z-0"
                  />
                )}
                <tab.icon className="relative z-10 w-5 h-5" />
                <span className="relative z-10">{tab.label}</span>
              </motion.button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'roadmap' && (
              <motion.div
                key="roadmap"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
              >
                {roadmap.map((quarter) => (
                  <motion.div
                    key={quarter.id}
                    onClick={() => setSelectedQuarter(quarter)}
                    className="bg-white rounded-lg p-6 border border-border-color shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-deep-green font-fjalla">{quarter.name}</h3>
                      <Calendar className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm text-sage-green font-medium mb-4">{quarter.theme}</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">Deliverables</span>
                        <span className="font-bold text-deep-green">{quarter.deliverables.length}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">Milestones</span>
                        <span className="font-bold text-deep-green">{quarter.milestones.length}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                      <div className="sqsft-gradient-primary h-2 rounded-full" style={{ width: `${Math.random() * 100}%` }}></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {activeTab === 'summary' && (
              <motion.div
                key="summary"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <div className="space-y-6">
                  <div className="bg-white rounded-lg p-6 border border-border-color">
                    <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                      <Target className="w-6 h-6 mr-3 text-sage-green" />
                      Strategic Overview
                    </h3>
                    <p className="mb-4 text-text-secondary">{executiveSummary.overview}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-100 rounded-lg p-3">
                        <div className="text-sm text-text-secondary">Total Investment</div>
                        <div className="font-bold text-lg text-deep-green">{executiveSummary.totalInvestment}</div>
                      </div>
                      <div className="bg-gray-100 rounded-lg p-3">
                        <div className="text-sm text-text-secondary">Expected ROI</div>
                        <div className="text-sage-green font-bold text-lg">{executiveSummary.expectedROI}</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-border-color">
                    <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                      <Zap className="w-6 h-6 mr-3 text-royal-gold" />
                      Competitive Advantages
                    </h3>
                    <ul className="space-y-3">
                      {executiveSummary.competitiveAdvantages.map((advantage, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-sage-green mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">{advantage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-white rounded-lg p-6 border border-border-color">
                    <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                      <Globe className="w-6 h-6 mr-3 text-sage-green" />
                      Market Opportunity
                    </h3>
                    <div className="text-center mb-4">
                      <div className="text-3xl font-bold text-sage-green mb-1">$12.8B</div>
                      <div className="text-text-secondary">Addressable Market by 2028</div>
                    </div>
                    <p className="text-center text-text-secondary">{executiveSummary.marketOpportunity}</p>
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-border-color">
                    <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                      <BarChart3 className="w-6 h-6 mr-3 text-sage-green"/>
                      Financial Summary (Projected)
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left font-semibold pb-2 text-text-secondary">Year</th>
                          <th className="text-right font-semibold pb-2 text-text-secondary">Revenue</th>
                          <th className="text-right font-semibold pb-2 text-text-secondary">R&D Cost</th>
                          <th className="text-right font-semibold pb-2 text-text-secondary">Gross Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(executiveSummary.financialSummary).map(([year, data]) => (
                          <tr key={year} className="border-b last:border-none">
                            <td className="py-2 font-medium text-deep-green">{year}</td>
                            <td className="text-right py-2 text-text-secondary">{data.revenue}</td>
                            <td className="text-right py-2 text-text-secondary">{data.rdCost}</td>
                            <td className="text-right py-2 font-bold text-sage-green">{data.grossMargin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-border-color">
                    <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                      <Shield className="w-6 h-6 mr-3 text-red-600" />
                      Risk Mitigation
                    </h3>
                    <ul className="space-y-3">
                      {executiveSummary.riskMitigation.map((risk, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Shield className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary">{risk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'implementation' && (
              <motion.div
                key="implementation"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <div className="space-y-6">
                   <div className="bg-white rounded-lg p-6 border border-border-color">
                      <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                        <Users className="w-6 h-6 mr-3 text-sage-green" />
                        Governance & Team Structure
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-deep-green">Core Team</h4>
                          <p className="text-text-secondary">{implementationDetails.governance.coreTeam}</p>
                        </div>
                         <div>
                          <h4 className="font-semibold text-deep-green">Steering Committee</h4>
                          <p className="text-text-secondary">{implementationDetails.governance.steeringCommittee}</p>
                        </div>
                         <div>
                          <h4 className="font-semibold text-deep-green">Communication Cadence</h4>
                          <p className="text-text-secondary">{implementationDetails.governance.communication}</p>
                        </div>
                      </div>
                    </div>
                </div>
                 <div className="space-y-6">
                   <div className="bg-white rounded-lg p-6 border border-border-color">
                      <h3 className="text-xl font-bold text-deep-green mb-4 flex items-center font-fjalla">
                        <Rocket className="w-6 h-6 mr-3 text-sage-green" />
                        Go-to-Market Strategy
                      </h3>
                       <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-deep-green mb-2">Launch Tiers</h4>
                           <ul className="space-y-2 list-inside text-text-secondary">
                            {implementationDetails.goToMarket.launchTiers.map((tier, index) => (
                              <li key={index} dangerouslySetInnerHTML={{ __html: tier.replace(/\*\*(.*?)\*\*/g, '<strong class="text-deep-green">$1</strong>') }}></li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-deep-green mb-2">Key Activities</h4>
                           <ul className="space-y-2 list-disc list-inside text-text-secondary">
                            {implementationDetails.goToMarket.keyActivities.map((activity, index) => (
                              <li key={index}>{activity}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'documentation' && (
              <motion.div
                key="documentation"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                 <div className="bg-white rounded-lg p-6 border border-border-color">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left font-semibold pb-3 font-fjalla text-deep-green">Document</th>
                          <th className="text-left font-semibold pb-3 font-fjalla text-deep-green">Description</th>
                          <th className="text-center font-semibold pb-3 font-fjalla text-deep-green">Version</th>
                          <th className="text-center font-semibold pb-3 font-fjalla text-deep-green">Status</th>
                          <th className="text-right font-semibold pb-3 font-fjalla text-deep-green">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentationHub.map((doc) => (
                          <tr key={doc.title} className="border-b last:border-none">
                            <td className="py-4">
                              <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-lg bg-sage-green/10">
                                  <doc.icon className="w-5 h-5 text-sage-green" />
                                </div>
                                <span className="font-medium text-deep-green">{doc.title}</span>
                              </div>
                            </td>
                            <td className="py-4 text-text-secondary">{doc.desc}</td>
                            <td className="py-4 text-center text-text-secondary">{doc.version}</td>
                            <td className="py-4 text-center">
                               <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status.toLowerCase().replace(' ', '-'))}`}>{doc.status}</span>
                            </td>
                             <td className="py-4 text-right">
                               <button className="p-2 hover:bg-gray-100 rounded-lg">
                                 <Download className="w-5 h-5 text-gray-500" />
                               </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                 </div>
              </motion.div>
            )}

            {activeTab === 'architecture' && (
              <motion.div
                key="architecture"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ArchitectureLayers isEmbedded={true} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {selectedQuarter && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedQuarter(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-border-color"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-deep-green font-fjalla">{selectedQuarter.name} Details</h2>
                      <p className="text-sage-green font-medium">{selectedQuarter.theme}</p>
                    </div>
                    <button onClick={() => setSelectedQuarter(null)} className="text-gray-400 hover:text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-deep-green font-semibold mb-4 font-fjalla">Deliverables</h3>
                      <div className="space-y-3">
                        {selectedQuarter.deliverables.map((deliverable) => (
                          <div key={deliverable.id} className="bg-gray-50 rounded-lg p-4 border border-border-color">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 pr-4">
                                 <h4 className="text-deep-green font-medium">{deliverable.name}</h4>
                                 <p className="text-xs text-text-secondary">Owner: {deliverable.owner}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(deliverable.status)}`}>
                                {deliverable.status}
                              </span>
                            </div>
                            <p className="mb-3 text-sm text-text-secondary">{deliverable.description}</p>
                            
                            <div className="bg-white rounded p-2 border border-border-color">
                               <h5 className="text-xs font-semibold text-text-secondary mb-1">Key Assumptions</h5>
                               <ul className="text-xs list-disc list-inside space-y-1 text-text-secondary">
                                {deliverable.keyAssumptions.map((assumption, i) => <li key={i}>{assumption}</li>)}
                               </ul>
                            </div>
                            
                            <div className="flex items-center justify-between text-xs mt-3">
                              <span className="text-text-secondary">Effort: {deliverable.effort}</span>
                              <span className={`px-2 py-1 rounded-full border ${getPriorityColor(deliverable.priority)}`}>
                                {deliverable.priority}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-deep-green font-semibold mb-4 font-fjalla">KPIs</h3>
                      <div className="space-y-3">
                        {selectedQuarter.kpis.map((kpi) => (
                          <div key={kpi.name} className="bg-gray-50 rounded-lg p-4 border border-border-color">
                            <h4 className="text-deep-green font-medium mb-2">{kpi.name}</h4>
                            <div className="flex items-end justify-between">
                              <div>
                                <p className="text-xs text-text-secondary">Target</p>
                                <p className="text-sage-green font-bold text-lg">{kpi.target}</p>
                              </div>
                              <div>
                                <p className="text-xs text-text-secondary">Current</p>
                                <p className="font-bold text-lg text-deep-green">{kpi.current}</p>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div className="sqsft-gradient-primary h-2 rounded-full" style={{ width: '50%' }}></div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <h3 className="text-deep-green font-semibold mt-6 mb-4 font-fjalla">Milestones</h3>
                      <div className="space-y-3">
                        {selectedQuarter.milestones.map((milestone) => (
                          <div key={milestone.id} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3 border border-border-color">
                            <CheckCircle className="w-5 h-5 text-sage-green" />
                            <div>
                              <p className="text-deep-green font-medium">{milestone.name}</p>
                              <p className="text-xs text-text-secondary">{milestone.date}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default DeliveryPhase;